import React from 'react';
import { format } from 'date-fns';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  ClipboardList,
  User,
  Calendar,
  AlertCircle,
  CheckCircle2,
  Clock,
  Circle,
  FileText,
  ExternalLink,
} from 'lucide-react';
import { taskService, type Task } from '@/services/taskService';
import { useToast } from '@/hooks/use-toast';
import { useNavigate } from 'react-router-dom';
import { useClinic } from '@/contexts/ClinicContext';
import { cn } from '@/lib/utils';

interface TaskDetailModalProps {
  task: Task | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onTaskUpdated?: () => void;
}

const priorityConfig: Record<string, { label: string; color: string; bgColor: string }> = {
  high: { label: 'High', color: '#ef4444', bgColor: '#fef2f2' },
  medium: { label: 'Medium', color: '#f97316', bgColor: '#fff7ed' },
  low: { label: 'Low', color: '#eab308', bgColor: '#fefce8' },
};

const statusConfig: Record<string, { label: string; icon: React.ElementType; color: string }> = {
  pending: { label: 'Pending', icon: Circle, color: 'text-slate-400' },
  in_progress: { label: 'In Progress', icon: Clock, color: 'text-blue-500' },
  completed: { label: 'Completed', icon: CheckCircle2, color: 'text-green-500' },
  overdue: { label: 'Overdue', icon: AlertCircle, color: 'text-red-500' },
};

const categoryLabels: Record<string, string> = {
  follow_up_visit: 'Follow-up Visit',
  lab_review: 'Lab Review',
  medication_check: 'Medication Check',
  patient_checkin: 'Patient Check-in',
  documentation: 'Documentation',
  other: 'Other',
};

const TaskDetailModal: React.FC<TaskDetailModalProps> = ({
  task,
  open,
  onOpenChange,
  onTaskUpdated,
}) => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const { isPractitioner } = useClinic();

  const getPortalBasePath = () => {
    if (isPractitioner) return '/practitioner-portal';
    // Admin + clinic_staff both live at root.
    return '';
  };

  const handleGoToPatient = () => {
    if (!task?.patient_id) return;
    const basePath = getPortalBasePath();
    navigate(`${basePath}/patients/${task.patient_id}`);
    onOpenChange(false);
  };

  const handleMarkComplete = async () => {
    if (!task) return;
    try {
      await taskService.markComplete(task.task_id);
      toast({
        title: 'Task completed',
        description: `"${task.title}" has been marked as completed.`,
      });
      onTaskUpdated?.();
      onOpenChange(false);
    } catch (error) {
      console.error('Error completing task:', error);
      toast({
        title: 'Error',
        description: 'Failed to update task status.',
        variant: 'destructive',
      });
    }
  };

  const effectiveStatus = task ? (task.effective_status || task.status) : 'pending';
  const priority = task ? (priorityConfig[task.priority] || priorityConfig.medium) : priorityConfig.medium;
  const status = statusConfig[effectiveStatus] || statusConfig.pending;
  const StatusIcon = status.icon;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ClipboardList className="h-5 w-5 text-orange-500" />
            Task Details
          </DialogTitle>
        </DialogHeader>

        {task && (
          <>
            <DialogBody className="space-y-4">
              {/* Title */}
              <div>
                <h3 className="text-base font-semibold text-slate-900">{task.title}</h3>
              </div>

              {/* Status & Priority row */}
              <div className="flex items-center gap-3 flex-wrap">
                <div className={cn('flex items-center gap-1.5 text-sm', status.color)}>
                  <StatusIcon className="h-4 w-4" />
                  <span className="font-medium">{status.label}</span>
                </div>
                <Badge
                  className="text-xs font-semibold"
                  style={{ backgroundColor: priority.bgColor, color: priority.color, border: `1px solid ${priority.color}30` }}
                >
                  {priority.label} Priority
                </Badge>
                <Badge variant="outline" className="text-xs">
                  {categoryLabels[task.category] || task.category}
                </Badge>
              </div>

              {/* Details grid */}
              <div className="space-y-3 rounded-lg border border-slate-100 bg-slate-50/50 p-3">
                {/* Due date */}
                <div className="flex items-center gap-2 text-sm">
                  <Calendar className="h-4 w-4 text-slate-400 flex-shrink-0" />
                  <span className="text-slate-500">Due:</span>
                  <span className="font-medium text-slate-700">
                    {format(new Date(task.due_date + 'T00:00:00'), 'MMMM d, yyyy')}
                  </span>
                </div>

                {/* Patient */}
                {task.patient && (
                  <div className="flex items-center gap-2 text-sm">
                    <User className="h-4 w-4 text-slate-400 flex-shrink-0" />
                    <span className="text-slate-500">Patient:</span>
                    <span className="font-medium text-slate-700">
                      {[task.patient.first_name, task.patient.middle_name, task.patient.last_name].filter(Boolean).join(' ')}
                    </span>
                  </div>
                )}

                {/* Assigned provider */}
                {task.assignedProvider && (
                  <div className="flex items-center gap-2 text-sm">
                    <User className="h-4 w-4 text-slate-400 flex-shrink-0" />
                    <span className="text-slate-500">Assigned to:</span>
                    <span className="font-medium text-slate-700">
                      {task.assignedProvider.first_name} {task.assignedProvider.last_name}
                      {task.assignedProvider.credentials && `, ${task.assignedProvider.credentials}`}
                    </span>
                  </div>
                )}

                {/* Source */}
                <div className="flex items-center gap-2 text-sm">
                  <FileText className="h-4 w-4 text-slate-400 flex-shrink-0" />
                  <span className="text-slate-500">Source:</span>
                  <span className="font-medium text-slate-700">
                    {task.source === 'ai_generated' ? 'AI Generated' : 'Manual'}
                  </span>
                </div>

                {/* Completed at */}
                {task.completed_at && (
                  <div className="flex items-center gap-2 text-sm">
                    <CheckCircle2 className="h-4 w-4 text-green-400 flex-shrink-0" />
                    <span className="text-slate-500">Completed:</span>
                    <span className="font-medium text-slate-700">
                      {format(new Date(task.completed_at), 'MMMM d, yyyy h:mm a')}
                    </span>
                  </div>
                )}
              </div>

              {/* Notes */}
              {task.notes && (
                <div>
                  <p className="text-xs font-medium text-slate-500 mb-1">Notes</p>
                  <p className="text-sm text-slate-700 whitespace-pre-wrap rounded-md border border-slate-100 bg-white p-2.5">
                    {task.notes}
                  </p>
                </div>
              )}
            </DialogBody>

            <DialogFooter>
              <Button variant="outline" size="sm" onClick={() => onOpenChange(false)}>
                Close
              </Button>
              {task.patient_id && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleGoToPatient}
                >
                  <ExternalLink className="h-4 w-4 mr-1.5" />
                  Go to Patient
                </Button>
              )}
              {effectiveStatus !== 'completed' && (
                <Button
                  size="sm"
                  onClick={handleMarkComplete}
                  className="bg-green-600 hover:bg-green-700"
                >
                  <CheckCircle2 className="h-4 w-4 mr-1.5" />
                  Mark Complete
                </Button>
              )}
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default TaskDetailModal;
